import type { DualTheme, Theme } from '../types';
import { sanitizeDualTheme } from './sanitize';

// packages/lyric-stage/src/theme/generateTheme.ts
// HTTP client for Folia-compatible /api/generate-theme endpoints.

export interface GenerateThemeOptions {
  /** Absolute or relative URL. Defaults to Folia Gemini endpoint. */
  endpoint?: string;
  isPureMusic?: boolean;
  songTitle?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error ?? '');
};

export const isMissingAiApiKeyError = (error: unknown) => {
  const message = getErrorMessage(error);
  return /(?:openai_api_key|gemini_api_key|api key)/i.test(message)
    && /(?:not configured|missing|configure)/i.test(message);
};

export const generateThemeFromLyrics = async (
  lyricsText: string,
  options: GenerateThemeOptions = {},
): Promise<DualTheme> => {
  const endpoint = options.endpoint ?? '/api/generate-theme';
  const fetchImpl = options.fetchImpl ?? fetch;

  const response = await fetchImpl(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      lyricsText,
      isPureMusic: options.isPureMusic ?? false,
      songTitle: options.songTitle,
    }),
    signal: options.signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorData.error || `Failed to generate theme (${response.status})`);
  }

  const dualTheme = await response.json();
  return sanitizeDualTheme(dualTheme);
};

export const selectTheme = (
  dualTheme: DualTheme,
  options: { isDaylight?: boolean } = {},
): Theme => (options.isDaylight ? dualTheme.light : dualTheme.dark);
